// Product detail — pricing / sourcing / inventory / lifecycle + linked solutions.
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : null);
const money = (v: any) => (v == null ? null : `$${Number(v).toLocaleString()}`);

function KV({ k, v }: { k: string; v: ReactNode }) {
  if (v === null || v === undefined || v === "" || v === false) return null;
  return (
    <div className="flex justify-between gap-4 border-b py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-right text-sm">{v}</span>
    </div>
  );
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const queryKey = ["/products", id];
  const { data, isLoading } = useQuery({ queryKey, queryFn: async () => (await api.get(`/products/${id}`)).data, enabled: !!id });
  async function patch(b: Record<string, unknown>) { await api.put(`/products/${id}`, b); qc.invalidateQueries({ queryKey }); }
  async function del() {
    if (!data || !confirm(`Delete product "${data.name}"?`)) return;
    await api.delete(`/products/${id}`);
    qc.invalidateQueries({ queryKey: ["/products"] });
    navigate("/products");
  }
  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Not found.</div>;
  const p = data;

  return (
    <>
      <PageHeader
        title={p.name}
        description={[p.code, p.category].filter(Boolean).join(" · ") || undefined}
        actions={
          <>
            <Button variant="outline" onClick={() => patch({ isActive: !p.isActive })}>{p.isActive ? "Deactivate" : "Activate"}</Button>
            <Button variant="outline" onClick={del}><Trash2 className="mr-1 h-4 w-4" />Delete</Button>
          </>
        }
      />
      <div className="space-y-4 p-6">
        {!p.isActive && <Badge variant="outline">Inactive</Badge>}
        {p.description && <Card><CardContent className="p-4 text-sm">{p.description}</CardContent></Card>}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader><CardTitle className="text-base">Pricing</CardTitle></CardHeader>
            <CardContent>
              <KV k="Unit price" v={money(p.unitPrice)} />
              <KV k="Currency" v={p.currency} />
              <KV k="Usage unit" v={p.usageUnit} />
              <KV k="Tax %" v={p.taxPct != null ? `${p.taxPct}%` : null} />
              <KV k="Commission %" v={p.commissionPct != null ? `${p.commissionPct}%` : null} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Sourcing</CardTitle></CardHeader>
            <CardContent>
              <KV k="Manufacturer" v={p.manufacturer} />
              <KV k="Vendor" v={p.vendor ? <Link className="hover:underline" to={`/accounts/${p.vendor.id}`}>{p.vendor.name}</Link> : null} />
              <KV k="Owner" v={p.owner?.name} />
              <KV k="Handler" v={p.handler?.name} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Inventory</CardTitle></CardHeader>
            <CardContent>
              <KV k="In stock" v={p.quantityInStock} />
              <KV k="Ordered" v={p.quantityOrdered} />
              <KV k="In demand" v={p.quantityInDemand} />
              <KV k="Reorder level" v={p.reorderLevel} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Lifecycle</CardTitle></CardHeader>
            <CardContent>
              <KV k="Sales start" v={fmtDate(p.salesStartDate)} />
              <KV k="Sales end" v={fmtDate(p.salesEndDate)} />
              <KV k="Support start" v={fmtDate(p.supportStartDate)} />
              <KV k="Support end" v={fmtDate(p.supportEndDate)} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Linked solutions</CardTitle></CardHeader>
            <CardContent>
              {p.solutions?.length ? (
                <div className="space-y-1">
                  {p.solutions.map((s: any) => (
                    <div key={s.id} className="text-sm"><Link className="hover:underline" to={`/solutions/${s.id}`}>{s.name}</Link>{s.category ? <span className="text-muted-foreground"> · {s.category}</span> : null}</div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No linked solutions.</p>}
            </CardContent>
          </Card>
          {p.tags?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Tags</CardTitle></CardHeader>
              <CardContent><div className="flex flex-wrap gap-1">{p.tags.map((t: string) => <Badge key={t} variant="outline">{t}</Badge>)}</div></CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
